import { logDataModels } from '../dataModelsLogger';
import { assetsInScope, descendantEmployeeIds, findUser, modelName, userContext } from './catalog';
import { emptyGroup, hasMeaningfulCondition } from './conditionNodes';
import { findDirectoryUser } from './policyDirectory';
import { conditionToSql, evaluateBoolean, parseExpression, validateExpression } from './parser';
import type {
  AccessOperation,
  Classification,
  GovernancePolicy,
  ImpactPreview,
  MaskAction,
  PolicyAsset,
  PolicyDefinitionMode,
  PolicyModel,
  PolicyRule,
  PolicyScope,
  PolicyTarget,
  SimulationRequest,
  SimulationResult,
  SubjectSelector,
  SyntheticUser,
  TimeWindow,
} from './types';

function wildcard(pattern: string, value: string): boolean {
  const escaped = pattern.replace(/[.+^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*').replace(/\?/g, '.');
  return new RegExp(`^${escaped}$`, 'i').test(value);
}

export function matchesDynamic(asset: PolicyAsset, target: PolicyTarget): Array<{ column: string; classification?: string }> {
  const match = target.match;
  const checks: Array<(columnName: string, classification?: string, tags?: string[], type?: string) => boolean> = [];
  if (match.classifications.length) {
    checks.push((_name, classification) => Boolean(classification && match.classifications.includes(classification as Classification)));
  }
  if (match.tags.length) {
    checks.push((_name, _classification, tags = []) => match.tags.some((tag) => tags.includes(tag)));
  }
  if (match.columnName.trim()) {
    checks.push((name) => name.toLowerCase() === match.columnName.trim().toLowerCase());
  }
  if (match.columnPattern.trim()) {
    checks.push((name) => wildcard(match.columnPattern.trim(), name));
  }
  if (match.dataTypes.length) {
    checks.push((_name, _c, _t, type) => Boolean(type && match.dataTypes.some((item) => item.toUpperCase() === type.toUpperCase())));
  }
  if (!checks.length && target.mode === 'dynamic') return [];
  return asset.columns
    .filter((column) => {
      const results = checks.map((check) => check(column.name, column.classification, column.tags, column.type));
      if (!results.length) return target.mode === 'all';
      return match.combinator === 'OR' ? results.some(Boolean) : results.every(Boolean);
    })
    .map((column) => ({ column: column.name, classification: column.classification }));
}

export function resolvePolicyTargets(policy: GovernancePolicy, assets: PolicyAsset[]): PolicyAsset[] {
  const scoped = assetsInScope(assets, policy.scope);
  const byClassification = (list: PolicyAsset[]) => {
    if (!policy.target.match.classifications.length) return list;
    return list.filter((asset) =>
      (asset.classification && policy.target.match.classifications.includes(asset.classification))
      || matchesDynamic(asset, policy.target).length > 0,
    );
  };
  if (policy.target.mode === 'current' || policy.scope.level === 'ASSET') {
    return byClassification(scoped.filter((asset) => asset.id === (policy.target.assetIds[0] || policy.scope.assetId)));
  }
  if (policy.target.mode === 'selected') {
    return byClassification(scoped.filter((asset) => policy.target.assetIds.includes(asset.id)));
  }
  if (policy.target.mode === 'dynamic') {
    return scoped.filter((asset) => {
      if (policy.type === 'ACCESS_CONTROL' || policy.type === 'TIME_BASED') {
        const classificationHit = !policy.target.match.classifications.length
          || (asset.classification && policy.target.match.classifications.includes(asset.classification))
          || matchesDynamic(asset, policy.target).length > 0;
        const tagHit = !policy.target.match.tags.length || policy.target.match.tags.some((tag) => asset.tags.includes(tag));
        return policy.target.match.combinator === 'OR' ? classificationHit || tagHit : classificationHit && tagHit;
      }
      return matchesDynamic(asset, policy.target).length > 0;
    });
  }
  return byClassification(scoped);
}

export function listingTargets(policy: GovernancePolicy, assets: PolicyAsset[]): PolicyAsset[] {
  const targeted = resolvePolicyTargets(policy, assets);
  if (!policy.target.columnRefs.length) return targeted;
  const ids = new Set(policy.target.columnRefs.map((ref) => ref.assetId));
  return targeted.filter((asset) => ids.has(asset.id));
}

export function policyAppliesToListing(policy: GovernancePolicy, scope: PolicyScope, assets: PolicyAsset[]): boolean {
  const targets = listingTargets(policy, assets);
  if (scope.level === 'ASSET') {
    return Boolean(scope.assetId && targets.some((asset) => asset.id === scope.assetId));
  }
  if (scope.level === 'MODEL') {
    const modelId = scope.modelId || '';
    return Boolean(modelId && targets.some((asset) => asset.modelIds.includes(modelId)));
  }
  return true;
}

export function resolveProtectedColumns(policy: GovernancePolicy, assets: PolicyAsset[]): Array<{ asset: PolicyAsset; column: string; classification?: string }> {
  const targeted = resolvePolicyTargets(policy, assets);
  if (policy.target.columnRefs.length && (policy.target.mode === 'selected' || policy.type === 'COLUMN_LEVEL')) {
    const explicit: Array<{ asset: PolicyAsset; column: string; classification?: string }> = [];
    policy.target.columnRefs.forEach((ref) => {
      const asset = targeted.find((item) => item.id === ref.assetId);
      const column = asset?.columns.find((item) => item.name === ref.column);
      if (asset && column) explicit.push({ asset, column: column.name, classification: column.classification });
    });
    if (explicit.length) return explicit;
  }
  return targeted.flatMap((asset) => matchesDynamic(asset, policy.target).map((column) => ({ asset, ...column })));
}

function subjectMatches(subject: SubjectSelector, user: SyntheticUser): boolean {
  if (subject.mode === 'everyone') return true;
  if (subject.mode === 'users') return subject.userIds.includes(user.id);
  if (subject.mode === 'roles') return subject.roles.some((role) => user.roles.includes(role) || user.role.toLowerCase() === role.toLowerCase());
  if (subject.mode === 'groups') return subject.groups.some((group) => user.groups.includes(group));
  if (subject.mode === 'attributes') {
    const actual = subject.attribute.key === 'department' ? user.department : subject.attribute.key === 'region' ? user.region : user.clearance;
    if (subject.attribute.operator === 'eq') return actual === subject.attribute.value;
    if (subject.attribute.operator === 'neq') return actual !== subject.attribute.value;
    return String(actual).includes(subject.attribute.value);
  }
  if (subject.expression.trim()) {
    const parsed = parseExpression(subject.expression);
    if (!parsed.ast) return false;
    const result = evaluateBoolean(parsed.ast, userContext(user));
    return result === true;
  }
  return true;
}

function maskCompatible(columnType: string, mask: MaskAction): string | null {
  const numeric = /INT|DECIMAL|NUMBER|FLOAT|BIGINT/i.test(columnType);
  if (numeric && ['full_string', 'partial_email', 'partial_phone', 'partial_first', 'partial_last'].includes(mask.kind)) {
    return `Mask ${mask.kind} is incompatible with ${columnType}`;
  }
  return null;
}

export function applyMask(value: unknown, mask: MaskAction): unknown {
  if (value == null) return value;
  const text = String(value);
  switch (mask.kind) {
    case 'full_null':
      return null;
    case 'full_fixed':
      return mask.value ?? '***';
    case 'full_string':
      return '*'.repeat(Math.max(text.length, 4));
    case 'partial_first':
      return `${text.slice(0, mask.n ?? 2)}${'*'.repeat(Math.max(0, text.length - (mask.n ?? 2)))}`;
    case 'partial_last':
      return `${'*'.repeat(Math.max(0, text.length - (mask.n ?? 4)))}${text.slice(-(mask.n ?? 4))}`;
    case 'partial_email': {
      const [local, domain] = text.split('@');
      if (!domain) return `${text.slice(0, 1)}***`;
      return `${local.slice(0, 1)}***@${domain}`;
    }
    case 'partial_phone':
      return text.replace(/\d(?=\d{4})/g, '*');
    case 'partial_custom':
      return (mask.value || '***').replace(/X/g, () => '*');
    case 'custom_hash':
      return `hash_${Math.abs(Array.from(text).reduce((acc, ch) => acc + ch.charCodeAt(0), 0)).toString(16)}`;
    case 'custom_expression':
      return `masked(${text.slice(0, 2)})`;
    case 'deny_column':
    case 'deny_asset':
      return '[DENIED]';
    case 'none':
      return value;
    default:
      return value;
  }
}

function windowActive(window: TimeWindow, timestamp: string): { active: boolean; error?: string } {
  const zone = window.timeZone || 'UTC';
  let date: Date;
  try {
    date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return { active: false, error: 'Invalid simulated timestamp' };
  } catch {
    return { active: false, error: 'Invalid simulated timestamp' };
  }
  if (window.start && window.end && window.start > window.end) return { active: false, error: 'Start must be before end' };
  if (window.start && date < new Date(window.start)) return { active: false };
  if (window.end && date > new Date(window.end)) return { active: false };
  const local = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const weekday = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(local.find((part) => part.type === 'weekday')?.value ?? '');
  const hour = local.find((part) => part.type === 'hour')?.value ?? '00';
  const minute = local.find((part) => part.type === 'minute')?.value ?? '00';
  const hhmm = `${hour}:${minute}`;
  if (window.daysOfWeek.length && !window.daysOfWeek.includes(weekday)) return { active: false };
  if (window.dailyStart && window.dailyEnd) {
    if (window.dailyStart > window.dailyEnd) return { active: false, error: 'Daily start must be before daily end' };
    if (hhmm < window.dailyStart || hhmm > window.dailyEnd) return { active: false };
  }
  return { active: true };
}

export function ruleDefinitionMode(rule: PolicyRule): PolicyDefinitionMode {
  if (rule.definitionMode === 'simple' || rule.definitionMode === 'complex') return rule.definitionMode;
  return rule.expression?.trim() ? 'complex' : 'simple';
}

export function policyDefinitionMode(policy: GovernancePolicy): PolicyDefinitionMode {
  if (policy.type === 'ROW_LEVEL' || policy.type === 'COLUMN_LEVEL') {
    return policy.rules.some((rule) => ruleDefinitionMode(rule) === 'complex') ? 'complex' : 'simple';
  }
  return policy.advancedExpression.trim() ? 'complex' : 'simple';
}

function ruleExpression(rule: PolicyRule, fallback: string): string {
  if (rule.action.type === 'ROW_FILTER' || rule.action.type === 'COLUMN') {
    if (ruleDefinitionMode(rule) === 'complex') return rule.expression?.trim() || '';
    return rule.condition && hasMeaningfulCondition(rule.condition) ? conditionToSql(rule.condition) : '';
  }
  if (rule.expression?.trim()) return rule.expression;
  if (rule.condition) return conditionToSql(rule.condition);
  return fallback;
}

function customMaskSql(mask: MaskAction): string {
  return (mask.expression || mask.value || '').trim();
}

function evaluateWhen(
  expression: string,
  row: Record<string, unknown>,
  user: SyntheticUser,
  resource: Record<string, unknown>,
): boolean | 'unsupported' {
  if (!expression.trim()) return true;
  const parsed = parseExpression(expression);
  if (!parsed.ast) return 'unsupported';
  return evaluateBoolean(parsed.ast, { ...row, ...userContext(user), ...resource });
}

function evaluateRowFilter(expression: string, row: Record<string, unknown>, user: SyntheticUser, resource: Record<string, unknown>): boolean | 'unsupported' {
  const parsed = parseExpression(expression);
  if (!parsed.ast) return 'unsupported';
  const ctx = { ...row, ...userContext(user), ...resource };
  return evaluateBoolean(parsed.ast, ctx);
}

export function computeImpact(
  policy: GovernancePolicy,
  assets: PolicyAsset[],
  models: PolicyModel[],
  inherited: GovernancePolicy[],
): ImpactPreview {
  const scoped = assetsInScope(assets, policy.scope);
  const matched = resolvePolicyTargets(policy, assets);
  const columns = resolveProtectedColumns(policy, assets);
  const users = policy.rules.flatMap((rule) => {
    if (rule.subject.mode === 'everyone') return ['Everyone'];
    if (rule.subject.mode === 'users') return rule.subject.userIds;
    if (rule.subject.mode === 'roles') return rule.subject.roles;
    if (rule.subject.mode === 'groups') return rule.subject.groups;
    return [rule.subject.mode];
  });
  const typeIncompatibilities: string[] = [];
  const missingClassifications: string[] = [];
  matched.forEach((asset) => {
    asset.columns.forEach((column) => {
      if (!column.classification) missingClassifications.push(`${asset.name}.${column.name}`);
    });
    policy.rules.forEach((rule) => {
      const action = rule.action;
      if (action.type === 'COLUMN' || action.type === 'SENSITIVE') {
        columns.filter((item) => item.asset.id === asset.id).forEach((item) => {
          const column = asset.columns.find((entry) => entry.name === item.column);
          if (!column) return;
          const issue = maskCompatible(column.type, action.mask);
          if (issue) typeIncompatibilities.push(`${asset.name}.${item.column}: ${issue}`);
        });
      }
    });
  });
  const conflicts = inherited
    .filter((parent) => parent.type === policy.type || (parent.type === 'SENSITIVE_DATA' && policy.type === 'COLUMN_LEVEL'))
    .filter((parent) => parent.rules.some((rule) => rule.action.type === 'ACCESS' && rule.action.effect === 'deny')
      || parent.rules.some((rule) => (rule.action.type === 'COLUMN' || rule.action.type === 'SENSITIVE') && rule.action.mask.kind === 'deny_column'))
    .filter((parent) => policy.rules.some((rule) => (rule.action.type === 'ACCESS' && rule.action.effect === 'allow')
      || ((rule.action.type === 'COLUMN' || rule.action.type === 'SENSITIVE') && rule.action.mask.kind !== 'deny_column' && rule.action.mask.kind !== 'deny_asset')))
    .map((parent) => `Child policy cannot weaken inherited restriction from ${parent.name}`);

  const unsupported: string[] = [];
  policy.rules.forEach((rule) => {
    const expr = ruleExpression(rule, policy.advancedExpression);
    if (expr.trim()) {
      const result = validateExpression(expr, matched.length ? matched : scoped);
      if (result.unsupported) unsupported.push(result.errors[0] || 'Expression cannot be simulated');
      else if (!result.ok) unsupported.push(...result.errors);
    }
  });
  if (policy.advancedExpression.trim()) {
    const result = validateExpression(policy.advancedExpression, matched.length ? matched : scoped);
    if (!result.ok) unsupported.push(...result.errors);
  }

  logDataModels('app', 'governance_impact_computed', {
    policyType: policy.type,
    assetsInScope: scoped.length,
    assetsMatched: matched.length,
    columns: columns.length,
  });

  return {
    assetsInScope: scoped.length,
    assetsMatched: matched.map((asset) => ({
      id: asset.id,
      name: asset.name,
      modelNames: asset.modelIds.map((id) => modelName(models, id)).filter(Boolean),
    })),
    columnsProtected: columns.map((item) => ({ asset: item.asset.name, column: item.column, classification: item.classification })),
    modelsAffected: Array.from(new Set(matched.flatMap((asset) => asset.modelIds.map((id) => modelName(models, id)).filter(Boolean)))),
    usersAffected: Array.from(new Set(users)),
    inherited: inherited.map((item) => `${item.name} (${item.scope.level})`),
    conflicts,
    missingClassifications: Array.from(new Set(missingClassifications)).slice(0, 12),
    typeIncompatibilities,
    unsupported: Array.from(new Set(unsupported)),
  };
}

function resourceVars(asset: PolicyAsset, scope: PolicyScope, classification?: string): Record<string, unknown> {
  return {
    'resource.vault_id': asset.vaultId,
    'resource.model_id': scope.modelId ?? asset.modelIds[0] ?? '',
    'resource.asset_id': asset.id,
    'resource.classification': classification ?? asset.classification ?? '',
    'resource.connection_type': asset.connectionType,
  };
}

export function simulatePolicy(
  draft: GovernancePolicy,
  inherited: GovernancePolicy[],
  assets: PolicyAsset[],
  request: SimulationRequest,
): SimulationResult {
  const user = findDirectoryUser(request.userId) ?? findUser(request.userId);
  if (!user) {
    return { decision: 'denied', explanation: 'Unknown user', origins: [], matchedConditions: [] };
  }
  const applicable = [...inherited, draft].filter((policy) => policy.status !== 'INACTIVE');
  const asset = assets.find((item) => item.id === request.assetId) ?? resolvePolicyTargets(draft, assets)[0];
  if (!asset) {
    return { decision: 'limited', explanation: 'No target asset is in the current ownership scope.', origins: [], matchedConditions: [] };
  }

  const origins: SimulationResult['origins'] = [];
  const matchedConditions: string[] = [];
  let denied = false;
  let expired = false;
  let allowedByRule = false;
  let mask: MaskAction | undefined;
  let maskWhen = '';
  let clearExemption = false;
  let rowExpression = '';

  applicable.forEach((policy) => {
    const targets = resolvePolicyTargets(policy, assets);
    if (!targets.some((item) => item.id === asset.id)) return;
    policy.rules.forEach((rule) => {
      if (!subjectMatches(rule.subject, user)) return;
      if (rule.action.type === 'ACCESS') {
        if (!rule.action.operations.includes(request.operation)) return;
        const expr = ruleExpression(rule, policy.advancedExpression);
        if (expr.trim()) {
          const parsed = parseExpression(expr);
          if (!parsed.ast) {
            origins.push({ policyId: policy.id, policyName: policy.name, level: policy.scope.level, reason: 'Expression cannot be simulated' });
            return;
          }
          const ok = evaluateBoolean(parsed.ast, {
            ...userContext(user),
            'request.operation': request.operation,
            'request.timestamp': request.timestamp,
            'request.channel': user.channel,
            'request.environment': 'prod',
            ...resourceVars(asset, policy.scope),
          });
          if (ok === 'unsupported') return;
          if (!ok) return;
          matchedConditions.push(expr);
        }
        if (rule.action.effect === 'deny') {
          denied = true;
          origins.push({ policyId: policy.id, policyName: policy.name, level: policy.scope.level, reason: `Deny ${request.operation}` });
        } else {
          allowedByRule = true;
          origins.push({ policyId: policy.id, policyName: policy.name, level: policy.scope.level, reason: `Allow ${request.operation}` });
        }
      }
      if (rule.action.type === 'TIME') {
        const window = windowActive(rule.action.window, request.timestamp);
        if (window.error) {
          origins.push({ policyId: policy.id, policyName: policy.name, level: policy.scope.level, reason: window.error });
          return;
        }
        if (rule.action.effect === 'expire' && rule.action.window.end && new Date(request.timestamp) > new Date(rule.action.window.end)) {
          expired = true;
          denied = true;
          origins.push({ policyId: policy.id, policyName: policy.name, level: policy.scope.level, reason: 'Access expired' });
        } else if (rule.action.effect === 'deny_window' && window.active) {
          denied = true;
          origins.push({ policyId: policy.id, policyName: policy.name, level: policy.scope.level, reason: 'Denied during specified window' });
        } else if (rule.action.effect === 'allow_window') {
          if (window.active) {
            allowedByRule = true;
            origins.push({ policyId: policy.id, policyName: policy.name, level: policy.scope.level, reason: 'Allowed during specified window' });
          } else {
            denied = true;
            origins.push({ policyId: policy.id, policyName: policy.name, level: policy.scope.level, reason: 'Outside allowed window' });
          }
        }
      }
      if (rule.action.type === 'COLUMN' || rule.action.type === 'SENSITIVE') {
        const columns = resolveProtectedColumns(policy, assets).filter((item) => item.asset.id === asset.id);
        const focus = request.column ? columns.filter((item) => item.column === request.column) : columns;
        if (!focus.length) return;
        if (hasExceptAudience(rule.except) && subjectMatches(rule.except, user)) {
          clearExemption = true;
          origins.push({ policyId: policy.id, policyName: policy.name, level: policy.scope.level, reason: 'Except audience sees the clear value' });
          return;
        }
        if (rule.action.mask.kind === 'deny_column' || rule.action.mask.kind === 'deny_asset') {
          denied = true;
          origins.push({ policyId: policy.id, policyName: policy.name, level: policy.scope.level, reason: 'Deny column or asset access' });
        } else if (rule.action.mask.kind === 'none') {
          clearExemption = true;
          origins.push({ policyId: policy.id, policyName: policy.name, level: policy.scope.level, reason: 'No extra mask' });
        } else if (!mask || mask.kind !== 'deny_column') {
          const nextMask = strongerMask(mask, rule.action.mask);
          if (nextMask === rule.action.mask) maskWhen = ruleExpression(rule, '');
          mask = nextMask;
          origins.push({ policyId: policy.id, policyName: policy.name, level: policy.scope.level, reason: `Apply ${rule.action.mask.kind}` });
        }
      }
      if (rule.action.type === 'ROW_FILTER') {
        const expr = ruleExpression(rule, '');
        if (!expr.trim()) return;
        rowExpression = rowExpression ? `${rowExpression} AND (${expr})` : `(${expr})`;
        matchedConditions.push(expr);
        origins.push({
          policyId: policy.id,
          policyName: policy.name,
          level: policy.scope.level,
          reason: rule.subject.mode === 'everyone' ? 'Row filter combined with AND' : `Row filter for ${rule.subject.mode}`,
        });
      }
    });
  });

  if (denied || expired) {
    const denyOrigin = origins.find((item) => /deny|expired|outside/i.test(item.reason));
    return {
      decision: 'denied',
      explanation: expired
        ? `${user.name} cannot ${request.operation.toLowerCase()} ${asset.name} because access has expired.`
        : denyOrigin
          ? `${user.name} cannot ${request.operation.toLowerCase()} ${asset.name} because a Deny rule from ${denyOrigin.policyName} applies.`
          : `${user.name} cannot ${request.operation.toLowerCase()} ${asset.name}. Deny takes precedence over Allow.`,
      origins,
      matchedConditions,
    };
  }

  if (rowExpression) {
    const validation = validateExpression(rowExpression, [asset]);
    if (validation.unsupported || !validation.ok) {
      return {
        decision: 'limited',
        explanation: 'This row filter is outside the supported simulation subset.',
        origins,
        matchedConditions,
        unsupported: validation.errors[0] || 'Expression cannot be simulated',
      };
    }
    const allowedRows: Array<Record<string, unknown>> = [];
    const excludedRows: Array<Record<string, unknown>> = [];
    let filterUnsupported = false;
    asset.rows.forEach((row) => {
      const result = evaluateRowFilter(rowExpression, row, user, {
        ...resourceVars(asset, draft.scope),
        'request.operation': request.operation,
        'request.timestamp': request.timestamp,
        'request.channel': user.channel,
        'request.environment': 'prod',
      });
      if (result === 'unsupported') {
        filterUnsupported = true;
        return;
      }
      if (result === true) allowedRows.push(row);
      else excludedRows.push(row);
    });
    if (filterUnsupported) {
      return {
        decision: 'limited',
        explanation: 'This row filter is outside the supported simulation subset.',
        origins,
        matchedConditions,
        unsupported: 'Expression cannot be simulated',
      };
    }
    return {
      decision: 'filtered',
      explanation: `Row filters from the current and inherited policies are combined with AND. ${allowedRows.length} of ${asset.rows.length} sample rows remain visible for ${user.name}.`,
      origins,
      matchedConditions,
      allowedRows,
      excludedRows,
      rowCounts: { total: asset.rows.length, allowed: allowedRows.length, filtered: excludedRows.length },
    };
  }

  if (clearExemption) {
    mask = undefined;
    logDataModels('app', 'governance_mask_exemption', {
      userId: user.id,
      assetId: asset.id,
      column: request.column ?? null,
    });
  }

  if (mask) {
    const columns = request.column
      ? [request.column]
      : resolveProtectedColumns(draft, assets).filter((item) => item.asset.id === asset.id).map((item) => item.column);
    const samples = asset.rows.length ? asset.rows : [{}];
    const resource = {
      ...resourceVars(asset, draft.scope),
      'request.operation': request.operation,
      'request.timestamp': request.timestamp,
      'request.channel': user.channel,
      'request.environment': 'prod',
    };
    let whenUnsupported = false;
    const beforeAfter = samples.slice(0, 12).flatMap((row) => (
      columns
        .filter((column) => column in row || samples.length === 1)
        .map((column) => {
          const when = evaluateWhen(maskWhen, row, user, resource);
          if (when === 'unsupported') whenUnsupported = true;
          return {
            column,
            before: row[column],
            after: when === true ? applyMask(row[column], mask as MaskAction) : row[column],
          };
        })
    ));
    const preview = beforeAfter.map(({ column, before, after }) => ({ column, before, after }));
    const anyMasked = preview.some((item) => item.after !== item.before);
    if (whenUnsupported) {
      return {
        decision: 'limited',
        explanation: `The mask condition for ${user.name} uses a subquery or operator this simulator cannot evaluate. Query-time enforcement still wraps the mask in CASE WHEN.`,
        origins,
        matchedConditions: maskWhen ? [...matchedConditions, maskWhen] : matchedConditions,
        beforeAfter: preview,
        unsupported: 'When condition cannot be simulated',
      };
    }
    if (!anyMasked && maskWhen.trim()) {
      return {
        decision: 'allowed',
        explanation: `${user.name} sees the clear value because the mask when-condition is false on the sample rows.`,
        origins,
        matchedConditions: [...matchedConditions, maskWhen],
        beforeAfter: preview,
      };
    }
    return {
      decision: 'masked',
      explanation: maskWhen.trim()
        ? `Values are masked for ${user.name} only on rows where the when-condition is true. Other rows stay clear.`
        : `Effective restriction uses deny-over-mask precedence. Sample values for ${user.name} are transformed by the strongest applicable mask.`,
      origins,
      matchedConditions: maskWhen ? [...matchedConditions, maskWhen] : matchedConditions,
      beforeAfter: preview,
    };
  }

  if (draft.type === 'ACCESS_CONTROL' || draft.type === 'TIME_BASED') {
    if (!allowedByRule) {
      return {
        decision: 'denied',
        explanation: `${user.name} cannot ${request.operation.toLowerCase()} ${asset.name} because no Allow rule matches.`,
        origins,
        matchedConditions,
      };
    }
    const denyRules = draft.rules.filter((rule) => rule.action.type === 'ACCESS' && rule.action.effect === 'deny');
    const skipped = denyRules.find((rule) => !subjectMatches(rule.subject, user));
    const extra = skipped ? ` because ${user.name === 'Alice' ? 'she' : user.name === 'Emma' ? 'she' : 'they'} ${skipped.subject.mode === 'roles' ? `is not in the ${skipped.subject.roles[0] ?? 'selected'} role` : skipped.subject.mode === 'groups' ? `is not in the ${skipped.subject.groups[0] ?? 'selected'} group` : 'does not match a Deny audience'}` : '';
    return {
      decision: 'allowed',
      explanation: `${user.name} can ${request.operation.toLowerCase()} ${asset.name}${extra}.`,
      origins,
      matchedConditions,
    };
  }

  return {
    decision: 'allowed',
    explanation: clearExemption
      ? `${user.name} sees the clear value because a No extra mask rule matches.`
      : 'No restrictive rule matched this synthetic request.',
    origins,
    matchedConditions,
  };
}

function strongerMask(current: MaskAction | undefined, next: MaskAction): MaskAction {
  if (!current) return next;
  const rank = (mask: MaskAction) => {
    if (mask.kind === 'deny_column' || mask.kind === 'deny_asset') return 4;
    if (mask.kind.startsWith('full') || mask.kind === 'custom_hash') return 3;
    if (mask.kind.startsWith('partial') || mask.kind === 'custom_expression') return 2;
    return 1;
  };
  return rank(next) >= rank(current) ? next : current;
}

export function hierarchyExpression(): string {
  return "employee_id = ${user.employee_id}\nOR employee_id IN ${user.descendant_employee_ids}";
}

export function relatedHierarchyNote(user: SyntheticUser): string {
  const ids = descendantEmployeeIds(user.employeeId);
  return ids.length
    ? `${user.name} can see ${user.employeeId} and descendants ${ids.join(', ')} using the in-memory employee hierarchy.`
    : `${user.name} has no descendants in the synthetic hierarchy.`;
}

export function emptyTarget(): PolicyTarget {
  return {
    mode: 'all',
    assetIds: [],
    columnRefs: [],
    match: { combinator: 'AND', classifications: [], tags: [], columnName: '', columnPattern: '', dataTypes: [] },
  };
}

export function emptySubject(): SubjectSelector {
  return { mode: 'everyone', userIds: [], roles: [], groups: [], attribute: { key: 'department', operator: 'neq', value: '' }, expression: '' };
}

export function hasExceptAudience(subject: SubjectSelector | undefined): subject is SubjectSelector {
  if (!subject || subject.mode === 'everyone') return false;
  if (subject.mode === 'users') return subject.userIds.length > 0;
  if (subject.mode === 'roles') return subject.roles.length > 0;
  if (subject.mode === 'groups') return subject.groups.length > 0;
  if (subject.mode === 'attributes') return Boolean(subject.attribute.key.trim() && subject.attribute.value.trim());
  return Boolean(subject.expression.trim());
}

export function emptyWindow(): TimeWindow {
  return { start: '', end: '', daysOfWeek: [], dailyStart: '', dailyEnd: '', timeZone: 'America/New_York' };
}

export function defaultRule(type: GovernancePolicy['type']): PolicyRule {
  const id = `rule-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  if (type === 'ROW_LEVEL') {
    return {
      id,
      subject: emptySubject(),
      action: { type: 'ROW_FILTER' },
      definitionMode: 'simple',
      condition: emptyGroup(id),
    };
  }
  if (type === 'COLUMN_LEVEL') {
    return {
      id,
      subject: emptySubject(),
      action: { type: 'COLUMN', mask: { kind: 'partial_email', n: 1 } },
      definitionMode: 'simple',
      condition: emptyGroup(id),
    };
  }
  if (type === 'ACCESS_CONTROL') {
    return { id, subject: emptySubject(), action: { type: 'ACCESS', effect: 'allow', operations: ['SELECT'] } };
  }
  if (type === 'TIME_BASED') {
    return { id, subject: emptySubject(), action: { type: 'TIME', effect: 'expire', window: emptyWindow() } };
  }
  return { id, subject: emptySubject(), action: { type: 'SENSITIVE', mask: { kind: 'full_string' } } };
}

export function emptyPolicy(scope: PolicyScope, type: GovernancePolicy['type'] = 'ROW_LEVEL'): GovernancePolicy {
  return {
    id: '',
    name: '',
    description: '',
    type,
    status: 'DRAFT',
    scope,
    target: { ...emptyTarget(), mode: scope.level === 'ASSET' ? 'current' : 'all', assetIds: scope.assetId ? [scope.assetId] : [] },
    rules: [defaultRule(type)],
    advancedExpression: '',
    policyKey: '',
    generateId: true,
    version: 1,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

export function summarizeAppliesTo(policy: GovernancePolicy, assets: PolicyAsset[]): string {
  const matched = resolvePolicyTargets(policy, assets);
  if (policy.target.mode === 'all') return 'All assets in scope';
  if (policy.target.mode === 'dynamic') {
    const labels = [
      ...policy.target.match.classifications,
      ...policy.target.match.tags,
      policy.target.match.columnPattern,
    ].filter(Boolean);
    return labels.join(', ') || 'Dynamic match';
  }
  if (matched.length === 1) return matched[0].name;
  if (matched.length) return `${matched.length} assets`;
  return 'No matching assets';
}

export function validatePolicyDraft(policy: GovernancePolicy, assets: PolicyAsset[]): string[] {
  const errors: string[] = [];
  if (policy.name.trim().length < 2) errors.push('Policy name is required.');
  const matched = resolvePolicyTargets(policy, assets);
  if (!matched.length && policy.target.mode !== 'all') errors.push('Select at least one target in the current ownership scope.');
  if (policy.target.mode === 'dynamic' && !policy.target.match.classifications.length && !policy.target.match.tags.length && !policy.target.match.columnName && !policy.target.match.columnPattern && !policy.target.match.dataTypes.length) {
    errors.push('Dynamic matching needs a classification, tag, name, pattern, or data type.');
  }
  policy.rules.forEach((rule, index) => {
    if (rule.action.type === 'ACCESS' && !rule.action.operations.length) errors.push(`Rule ${index + 1} needs at least one operation.`);
    if (rule.action.type === 'TIME') {
      const window = rule.action.window;
      if (window.start && window.end && window.start > window.end) errors.push('Time range start must be before end.');
      if (rule.action.effect === 'expire' && !window.end) errors.push('Expiration needs an end date.');
    }
    if (rule.subject.mode === 'users' && !rule.subject.userIds.length) errors.push(`Rule ${index + 1} has no selected users.`);
    if (rule.subject.mode === 'roles' && !rule.subject.roles.length) errors.push(`Rule ${index + 1} has no selected roles.`);
    if (rule.subject.mode === 'groups' && !rule.subject.groups.length) errors.push(`Rule ${index + 1} has no selected groups.`);
    if (rule.except?.mode === 'users' && !rule.except.userIds.length) errors.push(`Rule ${index + 1} except has no selected users.`);
    if (rule.except?.mode === 'roles' && !rule.except.roles.length) errors.push(`Rule ${index + 1} except has no selected roles.`);
    if (rule.except?.mode === 'groups' && !rule.except.groups.length) errors.push(`Rule ${index + 1} except has no selected groups.`);
    const expr = ruleExpression(rule, '');
    if (policy.type === 'ROW_LEVEL') {
      if (ruleDefinitionMode(rule) === 'complex') {
        if (!rule.expression?.trim()) errors.push(`Rule ${index + 1} needs an expression.`);
      } else if (!hasMeaningfulCondition(rule.condition)) {
        errors.push(`Rule ${index + 1} needs a row condition.`);
      }
    }
    if (expr.trim()) {
      const result = validateExpression(expr, matched.length ? matched : assetsInScope(assets, policy.scope));
      if (!result.ok) errors.push(...result.errors);
    }
    const action = rule.action;
    if (policy.type === 'COLUMN_LEVEL' && (action.type === 'COLUMN' || action.type === 'SENSITIVE')) {
      if (action.mask.kind === 'custom_expression' && !customMaskSql(action.mask)) {
        errors.push(`Rule ${index + 1} needs custom mask SQL.`);
      }
    }
    if ((action.type === 'COLUMN' || action.type === 'SENSITIVE') && matched.length) {
      resolveProtectedColumns({ ...policy, rules: [rule] }, assets).forEach((item) => {
        const column = item.asset.columns.find((entry) => entry.name === item.column);
        if (!column) return;
        const issue = maskCompatible(column.type, action.mask);
        if (issue) errors.push(issue);
      });
    }
  });
  if (policy.type === 'COLUMN_LEVEL') {
    const hasColumns = policy.target.columnRefs.length > 0;
    const match = policy.target.match;
    const hasDynamic = policy.target.mode === 'dynamic'
      && Boolean(match.classifications.length || match.tags.length || match.columnName || match.columnPattern || match.dataTypes.length);
    if (!hasColumns && !hasDynamic) errors.push('Select at least one column to protect.');
  }
  if (policy.type !== 'ROW_LEVEL' && policy.advancedExpression.trim()) {
    const result = validateExpression(policy.advancedExpression, matched.length ? matched : assetsInScope(assets, policy.scope));
    if (!result.ok) errors.push(...result.errors);
  }
  return Array.from(new Set(errors));
}

export function applicablePolicies(policies: GovernancePolicy[], scope: PolicyScope, assets: PolicyAsset[] = []): GovernancePolicy[] {
  return policies.filter((policy) => {
    if (policy.scope.vaultId !== scope.vaultId) return false;
    const ownedHere = policy.scope.level === scope.level
      && (scope.level === 'VAULT'
        || (scope.level === 'MODEL' && policy.scope.modelId === scope.modelId)
        || (scope.level === 'ASSET' && policy.scope.assetId === scope.assetId));
    if (scope.level === 'VAULT') {
      return policy.scope.level === 'VAULT';
    }
    if (scope.level === 'MODEL') {
      if (!(policy.scope.level === 'VAULT' || (policy.scope.level === 'MODEL' && policy.scope.modelId === scope.modelId))) return false;
    } else if (!(policy.scope.level === 'VAULT' || (policy.scope.level === 'MODEL' && policy.scope.modelId === scope.modelId) || policy.scope.assetId === scope.assetId)) {
      return false;
    }
    if (ownedHere || !assets.length) return true;
    return policyAppliesToListing(policy, scope, assets);
  });
}
